// Review one attempt at a class assignment and grade it. The teacher reads the
// conversation, the AI's feedback, the AI's suggested score on the rubric and why, and the
// student's reflection; adjusts the score, adds a comment, and releases the grade. Until
// release the student sees no grade (and, when the school says so, no AI feedback either).
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Send, Sparkles } from 'lucide-react';
import { T } from '../gamification/shared';
import { createLogger } from '../../utils/logger';
import { uiLocale } from '../../i18n/language';
import { useSessionDetail } from '../../hooks/useSessionDetail';
import LoadingIndicator from '../loading-indicator/LoadingIndicator';
import ConfirmationModal from '../shared/ConfirmationModal';
import TeachingStepCard from '../sessions/components/TeachingStepCard';
import TranscriptSection from '../sessions/components/TranscriptSection';
import '../sessions/SessionDetailPage.scss';
import {
  AttemptReview,
  fetchAttemptReview,
  releaseAttemptGrade,
  saveAttemptReview,
} from '../../services/classService';
import {
  REVIEW_COMMENT_MAX_LENGTH,
  attemptState,
  formatScore,
  scoreProblem as findScoreProblem,
  scoreStep,
} from '../../utils/grading';
import {
  cardStyle,
  errorMessage,
  errorStyle,
  hintStyle,
  inputStyle,
  labelStyle,
  linkButtonStyle,
  noticeStyle,
  pageStyle,
  primaryButtonStyle,
  secondaryButtonStyle,
  sectionHeadingStyle,
  statusBadgeStyle,
  subtitleStyle,
  titleStyle,
} from './classesStyles';

const logger = createLogger('AttemptReviewPage');

const columnsStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 340px), 1fr))',
  gap: '1.25rem',
  alignItems: 'start',
};

const wrapStyle: React.CSSProperties = { overflowWrap: 'anywhere' };

const suggestionStyle: React.CSSProperties = {
  background: T.bg,
  border: `1px solid ${T.border}`,
  borderRadius: 8,
  padding: '0.75rem',
  marginBottom: '1rem',
};

const errorTextStyle: React.CSSProperties = { ...hintStyle, color: errorStyle.color };

/** The score field's value when the page opens. Only someone who can grade sees the AI
 *  suggestion there (as a starting point); a view-only reader sees a recorded score or
 *  nothing, so a suggestion is never mistaken for a teacher's grade. */
const startingScore = (review: AttemptReview): string => {
  const value = review.can_grade
    ? (review.final_score ?? review.draft_score ?? review.suggested_score)
    : (review.final_score ?? review.draft_score);
  return value === null || value === undefined ? '' : String(value);
};

/** The route: one keyed review per attempt, so moving between attempts (the history
 *  links) starts from a clean form instead of showing, or saving, the last one's. */
const AttemptReviewPage: React.FC = () => {
  const { classId = '', attemptId = '' } = useParams<{ classId: string; attemptId: string }>();
  return <AttemptReviewView key={`${classId}/${attemptId}`} classId={classId} attemptId={attemptId} />;
};

const AttemptReviewView: React.FC<{ classId: string; attemptId: string }> = ({ classId, attemptId }) => {
  const { t } = useTranslation();
  const [review, setReview] = useState<AttemptReview | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [score, setScore] = useState('');
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [confirmRelease, setConfirmRelease] = useState(false);
  const sessionId = review?.roleplay_session_id ?? undefined;
  const {
    session,
    error: sessionError,
    refetch: refetchSession,
    updateConversation,
  } = useSessionDetail(sessionId);

  const apply = (loaded: AttemptReview) => {
    setReview(loaded);
    setScore(startingScore(loaded));
    setComment(loaded.review_comment ?? '');
  };

  useEffect(() => {
    let ignore = false;
    fetchAttemptReview(classId, attemptId)
      .then(loaded => {
        if (!ignore) apply(loaded);
      })
      .catch(err => {
        if (ignore) return;
        logger.error('Could not load attempt', err);
        const status = (err as { status?: number }).status;
        setLoadError(status === 403 || status === 404 ? t('classReview.noAccess') : t('classReview.couldNotLoadAttempt'));
      });
    return () => {
      ignore = true;
    };
  }, [classId, attemptId, t]);

  const run = useCallback(async (work: () => Promise<AttemptReview>, success: string, fallback: string) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      apply(await work());
      setNotice(success);
    } catch (err) {
      logger.error(fallback, err);
      setError(errorMessage(err, fallback));
    } finally {
      setBusy(false);
    }
  }, []);

  if (loadError) {
    return (
      <div style={pageStyle}>
        <div style={errorStyle} role="alert">{loadError}</div>
        <Link to={`/classes/${classId}`}>{t('classReview.backToClass')}</Link>
      </div>
    );
  }
  if (!review) return <LoadingIndicator message={t('classReview.loading')} />;

  const state = attemptState(review);
  const released = state === 'released';
  // The server refuses a release while the session runs; say so before the teacher tries.
  const practising = state === 'practising';
  const max = review.max_score;
  const typed = score.trim();
  const parsed = typed === '' ? null : Number(typed);
  const problem = findScoreProblem(score, max);
  const problemText =
    problem === 'range' ? t('classReview.scoreRange', { max }) : problem === 'decimals' ? t('classReview.scoreDecimals') : null;
  const trimmedComment = comment.trim() || null;
  // Re-releasing the grade the student already has would only notify them again.
  const unchangedRelease = released && parsed === review.final_score && trimmedComment === (review.review_comment ?? null);

  // Only what the teacher filled in is saved: an empty score field never wipes a
  // drafted score (the server keeps fields that are not sent).
  const saveDraft = () =>
    run(
      () => saveAttemptReview(classId, attemptId, parsed === null ? { comment: trimmedComment } : { score: parsed, comment: trimmedComment }),
      t('classReview.draftSaved'),
      t('classReview.couldNotSave')
    );

  const release = () => {
    setConfirmRelease(false);
    if (parsed === null) return;
    run(
      () => releaseAttemptGrade(classId, attemptId, { score: parsed, comment: trimmedComment }),
      released ? t('classReview.gradeUpdated') : t('classReview.gradeReleased'),
      t('classReview.couldNotRelease')
    );
  };

  const reviewPath = `/classes/${classId}/attempts/${attemptId}`;
  const sessionLinkState = { from: reviewPath, fromLabel: t('classReview.backToReview') };

  const feedbackBody = () => {
    if (!sessionId) return <p style={{ ...hintStyle, margin: 0 }}>{t('classReview.noSession')}</p>;
    if (sessionError) {
      return (
        <p style={{ ...errorTextStyle, margin: 0 }} role="alert">
          {t('classReview.couldNotLoadSession')}{' '}
          <button type="button" style={linkButtonStyle} onClick={() => refetchSession()}>
            {t('common.retry')}
          </button>
        </p>
      );
    }
    if (!session) return <p style={{ ...hintStyle, margin: 0 }}>{t('classReview.loadingSession')}</p>;
    return null;
  };

  return (
    <div style={pageStyle}>
      <Link to={`/classes/${classId}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.85rem', marginBottom: 12 }}>
        <ArrowLeft size={14} /> {t('classReview.backToClass')}
      </Link>
      <div style={{ marginBottom: '1.25rem', ...wrapStyle }}>
        <h1 id="tour-page-title" style={titleStyle}>
          {review.student_name || review.student_id}
        </h1>
        <p style={subtitleStyle}>
          {[review.assignment_title, review.content_name, t('classReview.attemptN', { attempt: review.attempt_no })]
            .filter(Boolean)
            .join(' · ')}{' '}
          <span style={statusBadgeStyle(released)}>{t(`classReview.states.${state}`)}</span>
        </p>
      </div>

      <div style={columnsStyle}>
        <div style={{ minWidth: 0 }}>
          <section id="tour-page-primary-action" style={cardStyle} aria-labelledby="grade-heading">
            <h2 id="grade-heading" style={sectionHeadingStyle}>
              {t('classReview.gradeTitle')}
            </h2>
            <div style={suggestionStyle}>
              <p style={{ margin: 0, fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: 6 }}>
                <Sparkles size={15} color={T.primary} />
                <strong>{t('classReview.aiSuggested')}:</strong> {formatScore(review.suggested_score, max)}
              </p>
              <p style={{ ...hintStyle, margin: '6px 0 0', ...wrapStyle }}>
                {review.suggestion_rationale ||
                  (review.suggested_score === null || review.suggested_score === undefined ? t('classReview.noSuggestion') : null)}
              </p>
            </div>

            <label htmlFor="grade-score" style={labelStyle}>
              {t('classReview.finalScore', { max })}
            </label>
            <input
              id="grade-score"
              type="number"
              min={0}
              max={max}
              step={scoreStep(max)}
              style={{ ...inputStyle, width: 120 }}
              value={score}
              disabled={!review.can_grade || busy}
              onChange={e => setScore(e.target.value)}
              aria-invalid={Boolean(problemText)}
              aria-describedby={problemText ? 'grade-score-problem' : undefined}
            />
            <span style={{ fontSize: '0.9rem', marginLeft: 6 }}>/ {max}</span>
            {problemText && (
              <p id="grade-score-problem" style={errorTextStyle}>
                {problemText}
              </p>
            )}

            <label htmlFor="grade-comment" style={{ ...labelStyle, marginTop: '0.9rem' }}>
              {t('classReview.comment')}
            </label>
            <textarea
              id="grade-comment"
              rows={4}
              maxLength={REVIEW_COMMENT_MAX_LENGTH}
              style={{ ...inputStyle, width: '100%' }}
              value={comment}
              disabled={!review.can_grade || busy}
              onChange={e => setComment(e.target.value)}
              placeholder={t('classReview.commentPlaceholder')}
            />

            {review.can_grade ? (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: '0.9rem' }}>
                {!released && (
                  <button
                    type="button"
                    style={secondaryButtonStyle}
                    disabled={busy || Boolean(problemText) || (parsed === null && trimmedComment === null)}
                    onClick={saveDraft}
                  >
                    {t('classReview.saveDraft')}
                  </button>
                )}
                <button
                  type="button"
                  id="release-grade"
                  style={primaryButtonStyle}
                  disabled={busy || parsed === null || Boolean(problemText) || practising || unchangedRelease}
                  title={practising ? t('classReview.stillPractising') : undefined}
                  onClick={() => setConfirmRelease(true)}
                >
                  <Send size={14} /> {released ? t('classReview.updateRelease') : t('classReview.release')}
                </button>
              </div>
            ) : (
              <p style={hintStyle}>{t('classReview.readOnly')}</p>
            )}
            {/* Next to the buttons, not at the top of the page: on a phone the top is a
                screen away from where the teacher pressed. */}
            {error && (
              <div style={{ ...errorStyle, margin: '0.75rem 0 0' }} role="alert">
                {error}
              </div>
            )}
            {notice && (
              <div style={{ ...noticeStyle, margin: '0.75rem 0 0' }} role="status">
                {notice}
              </div>
            )}
            <p style={hintStyle}>
              {released && review.released_at
                ? t('classReview.releasedOn', { date: new Date(review.released_at).toLocaleString(uiLocale()) })
                : practising
                  ? t('classReview.stillPractising')
                  : t('classReview.notYetVisible')}
            </p>
          </section>

          {(review.rubric || review.objectives.length > 0) && (
            <section style={cardStyle} aria-labelledby="rubric-heading">
              <h2 id="rubric-heading" style={sectionHeadingStyle}>
                {t('classReview.rubric', { max })}
              </h2>
              {review.rubric && <p style={{ whiteSpace: 'pre-wrap', fontSize: '0.88rem', margin: '0 0 8px', ...wrapStyle }}>{review.rubric}</p>}
              {review.objectives.length > 0 && (
                <ul style={{ margin: 0, paddingLeft: '1.2rem', fontSize: '0.88rem', ...wrapStyle }}>
                  {review.objectives.map((objective, index) => (
                    <li key={index}>{objective}</li>
                  ))}
                </ul>
              )}
            </section>
          )}

          <section style={cardStyle} aria-labelledby="reflection-heading">
            <h2 id="reflection-heading" style={sectionHeadingStyle}>
              {t('studentPractice.reflectionTitle')}
            </h2>
            {review.reflection ? (
              <dl style={{ margin: 0, fontSize: '0.88rem', ...wrapStyle }}>
                <dt style={labelStyle}>{t('studentPractice.wentWell')}</dt>
                <dd style={{ margin: '0 0 10px' }}>{review.reflection.went_well || '—'}</dd>
                <dt style={labelStyle}>{t('studentPractice.tryNext')}</dt>
                <dd style={{ margin: '0 0 10px' }}>{review.reflection.try_next || '—'}</dd>
                <dt style={labelStyle}>{t('studentPractice.confidence')}</dt>
                <dd style={{ margin: 0 }}>
                  {review.reflection.confidence ? t(`studentPractice.confidenceLevels.${review.reflection.confidence}`) : '—'}
                </dd>
              </dl>
            ) : (
              <p style={{ ...hintStyle, margin: 0 }}>{t('studentPractice.noReflection')}</p>
            )}
          </section>

          <section style={cardStyle} aria-labelledby="history-heading">
            <h2 id="history-heading" style={sectionHeadingStyle}>
              {t('classReview.history')}
            </h2>
            <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              {review.history.map(h => (
                <li key={h.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', padding: '6px 0', borderTop: `1px solid ${T.border}`, fontSize: '0.88rem' }}>
                  {h.id === review.id ? (
                    <strong>{t('classReview.attemptN', { attempt: h.attempt_no })}</strong>
                  ) : (
                    <Link to={`/classes/${classId}/attempts/${h.id}`}>{t('classReview.attemptN', { attempt: h.attempt_no })}</Link>
                  )}
                  <span style={{ color: T.muted }}>
                    {new Date(h.started_at).toLocaleDateString(uiLocale(), { month: 'short', day: 'numeric' })}
                    {' · '}
                    {h.released_at ? formatScore(h.final_score, h.max_score) : t(`classReview.states.${attemptState(h)}`)}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        </div>

        {/* The session page's own cards (steps, transcript), so names, search and
            translation read the same here as there. */}
        <div className="session-detail-page session-detail-page--embedded" style={{ minWidth: 0 }}>
          <div className="feedback-column">
            <section id="tour-page-content" className="feedback-card" aria-labelledby="ai-feedback-heading">
              <h3 id="ai-feedback-heading">{t('classReview.aiFeedback')}</h3>
              {feedbackBody() ??
                (session!.feedbackStatus === 'failed' ? (
                  <p style={{ ...errorTextStyle, margin: 0 }}>{t('classReview.feedbackFailed')}</p>
                ) : session!.feedbackStatus !== 'completed' ? (
                  <p style={{ ...hintStyle, margin: 0 }}>{t('classReview.feedbackPending')}</p>
                ) : (
                  <>
                    {session!.feedbackSummary && <p className="feedback-summary">{session!.feedbackSummary}</p>}
                    {session!.feedbackSteps.length > 0 && (
                      <div className="steps-grid">
                        {session!.feedbackSteps.map(step => (
                          <TeachingStepCard key={`${step.number}-${step.name}`} step={step} />
                        ))}
                      </div>
                    )}
                  </>
                ))}
              {sessionId && (
                <p style={hintStyle}>
                  <Link to={`/sessions/${sessionId}`} state={sessionLinkState}>
                    {t('classReview.openSession')}
                  </Link>
                </p>
              )}
            </section>

            <section className="feedback-card transcript-card" aria-labelledby="transcript-heading">
              <h3 id="transcript-heading">{t('classReview.transcript')}</h3>
              {feedbackBody() ??
                (session!.conversations.length === 0 ? (
                  <p style={{ ...hintStyle, margin: 0 }}>{t('classReview.noTranscript')}</p>
                ) : (
                  <TranscriptSection
                    conversations={session!.conversations}
                    sessionLanguage={session!.sessionLanguage}
                    studentProfileName={t('classReview.aiSpeaker')}
                    userName={review.student_name || t('classReview.studentSpeaker')}
                    transcriptionId={session!.transcriptionId}
                    onConversationUpdate={updateConversation}
                  />
                ))}
            </section>
          </div>
        </div>
      </div>

      <ConfirmationModal
        isOpen={confirmRelease}
        title={released ? t('classReview.updateReleaseTitle') : t('classReview.releaseTitle')}
        message={t('classReview.releaseMessage', {
          name: review.student_name || t('classReview.theStudent'),
          score: parsed ?? '',
          max,
        })}
        confirmText={released ? t('classReview.updateRelease') : t('classReview.release')}
        loading={busy}
        onCancel={() => setConfirmRelease(false)}
        onConfirm={release}
      />
    </div>
  );
};

export default AttemptReviewPage;
